import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { api, fileUrl } from '../lib/api';
import { PageHeader, Loading, EmptyState } from '../components/Shared';

const CATEGORY_LABELS = {
  all: 'All', ward8: 'Beautiful Ward 8', community: 'Community', city_hall: 'City Hall',
  campaign: 'Campaign', infrastructure: 'Infrastructure', proof_archive: 'Proof Archive',
};

export default function Gallery() {
  const [items, setItems] = useState([]);
  // Media > Photos > Beautiful Ward 8 deep-links here as /gallery?album=ward8
  const [params, setParams] = useSearchParams();
  const category = params.get('album') || params.get('category') || 'all';
  const community = params.get('community') || 'all';
  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === 'all') next.delete('album'); else next.set('album', c);
    next.delete('category');
    next.delete('community');
    setParams(next, { replace: true });
  };
  const setCommunity = (c) => {
    const next = new URLSearchParams(params);
    if (c === 'all') next.delete('community'); else next.set('community', c);
    setParams(next, { replace: true });
  };
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/gallery').then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Deep link from the homepage strip: /gallery?album=ward8&photo=<id> opens that photo
  const photoParam = params.get('photo');
  useEffect(() => {
    if (!photoParam || !items.length) return;
    const hit = items.find((i) => i.id === photoParam);
    if (hit) setLightbox(hit);
  }, [photoParam, items]);
  const closeLightbox = () => {
    setLightbox(null);
    if (params.get('photo')) {
      const next = new URLSearchParams(params);
      next.delete('photo');
      setParams(next, { replace: true });
    }
  };

  const cats = ['all', ...new Set(items.map((i) => i.category))];
  const inCategory = category === 'all' ? items : items.filter((i) => i.category === category);
  // Community tags (Beautiful Ward 8 photos carry the community they show)
  const communities = ['all', ...new Set(inCategory.flatMap((i) => i.communities || []))];
  const filtered = community === 'all' ? inCategory : inCategory.filter((i) => (i.communities || []).includes(community));

  if (loading) return <Loading />;

  return (
    <div data-testid="gallery-page">
      <PageHeader pageKey="gallery" kicker="GALLERY & PROOF ARCHIVE" title="Gallery" subtitle="Campaign photos, community moments, and the proof archive: documents and clippings behind the questions Steve has raised." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="gallery-filters">
          {cats.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`gallery-filter-${c}`} aria-pressed={category === c} className={`px-4 py-2 min-h-[40px] rounded-full text-sm font-bold border transition-colors duration-150 ${category === c ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#C9A84C]'}`}>
              {CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
        {communities.length > 2 && (
          <div className="flex flex-wrap items-center gap-2 mb-6 -mt-2" data-testid="gallery-community-filters" aria-label="Filter by community">
            <span className="text-xs font-bold uppercase tracking-widest text-[#24406f] mr-1 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Community</span>
            {communities.map((c) => (
              <button key={c} onClick={() => setCommunity(c)} data-testid={`gallery-community-${c === 'all' ? 'all' : c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} aria-pressed={community === c} className={`px-3 py-1.5 min-h-[36px] rounded-full text-xs font-bold border transition-colors duration-150 ${community === c ? 'bg-[#C9A84C] text-[#1a2744] border-[#C9A84C]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#C9A84C]'}`}>
                {c === 'all' ? 'All communities' : c}
              </button>
            ))}
          </div>
        )}
        {filtered.length === 0 ? (
          <EmptyState title={community !== 'all' ? `No photos tagged ${community} yet` : 'No photos in this category yet'} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <button key={item.id} onClick={() => setLightbox(item)} className="text-left" data-testid="gallery-item">
                <Card className="overflow-hidden card-lift">
                  <img src={fileUrl(item.image_url)} alt={item.title} className="h-56 w-full object-cover" />
                  <div className="p-4">
                    <p className="font-serif font-bold text-[#1a2744]">{item.title}</p>
                    {item.caption && <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{item.caption}</p>}
                    {(item.communities || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid="gallery-item-tags">
                        {item.communities.map((t) => <span key={t} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1a2744] bg-[#FBF7EE] border border-[#C9A84C]/50 rounded-full px-2 py-0.5"><MapPin className="h-3 w-3 text-[#8a6d2c]" />{t}</span>)}
                      </div>
                    )}
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && closeLightbox()}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {lightbox && (
            <div>
              <img src={fileUrl(lightbox.image_url)} alt={lightbox.title} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="p-5">
                <p className="font-serif font-bold text-[#1a2744] text-lg">{lightbox.title}</p>
                {lightbox.caption && <p className="text-sm text-slate-600 mt-1">{lightbox.caption}</p>}
                {(lightbox.communities || []).length > 0 && <p className="text-xs font-bold text-[#8a6d2c] mt-2 inline-flex items-center gap-1" data-testid="gallery-lightbox-tags"><MapPin className="h-3.5 w-3.5" /> {lightbox.communities.join(' · ')}</p>}
                {lightbox.photo_credit && <p className="text-xs text-slate-500 mt-1" data-testid="gallery-lightbox-credit">{lightbox.photo_credit}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
